import React from "react"
import { useTranslation } from "react-i18next"
import { faAddressBook, faClose } from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import ModalPortal from "../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import { ButtonIcon } from "../../components/Button/Button"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { AdminLandingLead } from "../../Objects/LandingLead/AdminLandingLead"

type Props = { lead: AdminLandingLead; onClose: () => void }

export const LandingLeadDetailsPopup: React.FC<Props> = ({ lead, onClose }) => {
  const { t } = useTranslation("adminLandingLeads")

  const basicFields = [
    { label: t("fields.name"), value: lead.name },
    { label: t("fields.business_name"), value: lead.businessName },
    { label: t("fields.email"), value: lead.email },
    { label: t("fields.phone"), value: lead.phoneNumber },
    { label: t("fields.source"), value: lead.trafficSource },
    { label: t("fields.form"), value: lead.formName },
    { label: t("fields.created_at"), value: new Date(lead.createdAt).toLocaleString("pt-BR") },
  ].filter((field) => Boolean(field.value))

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("details_title")}
              icon={faAddressBook}
              iconColor={THEME.colors.iconBGColor}
            >
              <ButtonIcon
                sizeNumber={SIZE.buttonCloseIconSize}
                bgColor={THEME.colors.buttonBGCloseColor}
                iconColor={THEME.colors.buttonFGCloseColor}
                icon={faClose}
                onClick={() => onClose()}
                size="small"
                descriptionKey="interactionDescriptions:pages.adminDashboard.landingLeadDetailsPopup.button.onClose"
              />
            </TitlePageResponsiveIconSmall>

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
              <div className="mt-3 max-h-[72vh] space-y-3 overflow-y-auto p-2">
                <section className="grid gap-2 sm:grid-cols-2">
                  {basicFields.map((field) => (
                    <DetailCard key={field.label} label={field.label} value={String(field.value)} />
                  ))}
                </section>

                {lead.answers.length > 0 && (
                  <section className="rounded-xl border-2 border-orange-200 bg-orange-50/40 p-3">
                    <h3 className="text-xs font-black uppercase tracking-wide text-orange-700">{t("answers_title")}</h3>

                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {lead.answers.map((answer, index) => (
                        <Animate type={AnimationTypes.fadeInTraslate} duration={`${1.5 + index * 0.5}`}>
                          <DetailCard key={`${answer.label}-${index}`} label={answer.label} value={answer.value} />
                        </Animate>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </Animate>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}

const DetailCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
    <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">{label}</p>

    <p className="mt-1 break-words text-sm font-semibold text-slate-800">{value}</p>
  </div>
)
